import React from 'react';
import { Link } from 'react-router-dom';
import ActionButton from '../components/ActionButton';
import { useCart } from '../context/CartContext';
import './cart.css';

const formatCurrency = (value) => `\u20a6${Number(value || 0).toLocaleString()}`;

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M5 12h14" />
  </svg>
);

const CartPage = () => {
  const { cartItems, updateItem, patchItem, removeItem, clearCart, totalQuantity, totalAmount } = useCart();

  const updateAddOnQuantity = (item, addOnId, delta) => {
    patchItem(item.id, item.type, (current) => {
      const currentQuantities = { ...(current.addOnQuantities || {}) };
      const nextQty = Math.max(0, (currentQuantities[addOnId] || 0) + delta);

      if (nextQty === 0) {
        delete currentQuantities[addOnId];
      } else {
        currentQuantities[addOnId] = nextQty;
      }

      const addOnOptions = current.addOnOptionDetails || {};
      const addOnIds = Object.keys(currentQuantities);
      const addOnNames = addOnIds
        .map((id) => {
          const option = addOnOptions[id];
          if (!option) return null;
          return `${currentQuantities[id]}x ${option.name}`;
        })
        .filter(Boolean);

      const addOnTotal = addOnIds.reduce((sum, id) => {
        const option = addOnOptions[id];
        return sum + (Number(option?.price || 0) * currentQuantities[id]);
      }, 0);

      const baseName = current.baseName || String(current.name || '').replace(/\s*\(\+.*\)$/, '');
      const currentAddOnTotal = Object.entries(current.addOnQuantities || {}).reduce((sum, [id, qty]) => {
        const option = addOnOptions[id];
        return sum + (Number(option?.price || 0) * Number(qty || 0));
      }, 0);
      const basePrice = Number(current.basePrice ?? Math.max(0, Number(current.price || 0) - currentAddOnTotal));

      return {
        addOnQuantities: currentQuantities,
        addOns: addOnIds,
        addOnNames,
        basePrice,
        price: basePrice + addOnTotal,
        name: addOnNames.length > 0 ? `${baseName} (+ ${addOnNames.join(', ')})` : baseName,
      };
    });
  };

  if (cartItems.length === 0) {
    return (
      <main className="cart-page">
        <div className="cart-empty">
          <div className="empty-icon" aria-hidden="true">Cart</div>
          <h1>Your cart is empty</h1>
          <p>Browse our menu and add items to get started</p>
          <div className="empty-actions">
            <ActionButton to="/menu" variant="primary" size="sm">
              Browse menu
            </ActionButton>
            <ActionButton to="/combos" variant="outline" size="sm">
              View combos
            </ActionButton>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <section className="cart-header">
        <h1>Your order</h1>
        <p className="cart-subtitle">{totalQuantity} item{totalQuantity !== 1 ? 's' : ''} in cart</p>
      </section>

      <div className="cart-container">
        <section className="cart-items-section">
          <div className="cart-items-list">
            {cartItems.map((item) => (
              <div key={`${item.id}-${item.type}`} className="cart-item">
                <div className="cart-item-thumb-wrap">
                  {item.image ? (
                    <img src={item.image} alt={item.baseName || item.name} className="cart-item-thumb" />
                  ) : (
                    <div className="cart-item-thumb cart-item-thumb-fallback" aria-hidden="true">VK</div>
                  )}
                </div>

                <div className="cart-item-info">
                  <h3>{item.name}</h3>
                  <p className="item-type">{item.type === 'combo' ? 'Combo' : 'Dish'}</p>
                  {item.details && <p className="item-note">Note: {item.details}</p>}

                  {item.type === 'dish' && item.addOnOptionDetails && Object.keys(item.addOnQuantities || {}).length > 0 && (
                    <div className="cart-item-addons">
                      {Object.entries(item.addOnQuantities).map(([addOnId, qty]) => {
                        const option = item.addOnOptionDetails[addOnId];
                        if (!option) return null;
                        return (
                          <div key={`${item.id}-${addOnId}`} className="cart-addon-row">
                            <div className="cart-addon-left">
                              <span className="cart-addon-name">{option.name}</span>
                              <span className="cart-addon-unit">+{formatCurrency(option.price)} each</span>
                            </div>
                            <div className="cart-addon-controls">
                              <button
                                type="button"
                                className="cart-addon-btn"
                                onClick={() => updateAddOnQuantity(item, addOnId, -1)}
                                aria-label={`Decrease ${option.name}`}
                              >
                                <MinusIcon />
                              </button>
                              <span className="cart-addon-qty">{qty}</span>
                              <button
                                type="button"
                                className="cart-addon-btn"
                                onClick={() => updateAddOnQuantity(item, addOnId, 1)}
                                aria-label={`Increase ${option.name}`}
                              >
                                <PlusIcon />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="item-price">{formatCurrency(item.price)}</p>
                </div>

                <div className="cart-item-quantity">
                  <button
                    onClick={() => updateItem(item.id, item.type, Math.max(1, item.quantity - 1))}
                    aria-label="Decrease quantity"
                    className="qty-btn"
                    type="button"
                  >
                    <MinusIcon />
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, item.type, Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="qty-input"
                    aria-label={`${item.name} quantity`}
                  />
                  <button
                    onClick={() => updateItem(item.id, item.type, item.quantity + 1)}
                    aria-label="Increase quantity"
                    className="qty-btn"
                    type="button"
                  >
                    <PlusIcon />
                  </button>
                </div>

                <div className="cart-item-total">
                  {formatCurrency(item.price * item.quantity)}
                </div>

                <button
                  onClick={() => removeItem(item.id, item.type)}
                  className="cart-item-remove"
                  aria-label={`Remove ${item.name} from cart`}
                  type="button"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="cart-summary-section">
          <div className="cart-summary">
            <h2>Order summary</h2>

            <div className="summary-rows">
              <div className="summary-row">
                <span>Subtotal ({totalQuantity} item{totalQuantity !== 1 ? 's' : ''})</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
              <div className="summary-row">
                <span>Delivery fee</span>
                <span>TBD</span>
              </div>
              <div className="summary-row">
                <span>Tax</span>
                <span>TBD</span>
              </div>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row summary-total">
              <span>Total</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>

            <div className="cart-actions">
              <ActionButton
                to="/checkout"
                variant="primary"
                size="sm"
                className="checkout-btn"
              >
                Proceed to checkout
              </ActionButton>
              <ActionButton
                variant="outline"
                size="sm"
                onClick={() => clearCart()}
                className="clear-btn"
              >
                Clear cart
              </ActionButton>
            </div>

            <Link to="/menu" className="continue-shopping">
              Continue shopping -&gt;
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};

export default CartPage;
